import React, { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Truck, Store, User, Loader2, Zap, ShoppingBag, Banknote, MapPin, MessageCircle } from "lucide-react";

const GoogleIcon = () => (
  <svg className="h-4 w-4" viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1Z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z" />
    <path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84Z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.3 9.14 5.38 12 5.38Z" />
  </svg>
);

export default function Auth() {
  const { setUser } = useAuth();
  const [mode, setMode] = useState("login");
  const [role, setRole] = useState("client");
  const [form, setForm] = useState({ email: "", password: "", name: "", phone: "", default_address: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "login") {
        const { data } = await api.post("/auth/login", { email: form.email, password: form.password });
        setUser(data.user);
      } else {
        const { data } = await api.post("/auth/register", { ...form, role });
        setUser(data.user);
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#0c111c]">
      <div className="pointer-events-none absolute -top-24 right-0 h-72 w-72 rounded-full bg-[#ff6a00]/20 blur-[120px]" />
      <div className="pointer-events-none absolute bottom-0 left-0 h-72 w-72 rounded-full bg-[#ff6a00]/10 blur-[120px]" />

      <div className="relative mx-auto flex min-h-screen max-w-md flex-col justify-center px-5 py-10">
        <div className="mb-8 text-center fade-up">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-[#ff6a00] shadow-[0_0_40px_rgba(255,106,0,0.5)]">
            <Truck className="h-8 w-8 text-white" strokeWidth={2.5} />
          </div>
          <h1 className="font-display text-4xl font-extrabold tracking-tight text-white">
            CHAP<span className="text-[#ff6a00]">CHAP</span>
          </h1>
          <p className="mt-1 text-sm text-slate-400">
            Livraison instantanée · un seul livreur · Île-de-France
          </p>
          <div className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-[#78350F] bg-[#2A1E0D] px-3 py-1 text-xs font-semibold text-[#FBBF24]">
            <Zap className="h-3 w-3" /> Paiement en espèces à la livraison
          </div>
        </div>

        <div className="rounded-3xl border border-[#1F293D] bg-[#151b2b] p-5 fade-up">
          <div className="mb-5 grid grid-cols-2 gap-1 rounded-xl bg-[#0c111c] p-1">
            {["login", "register"].map((m) => (
              <button
                key={m}
                data-testid={`auth-tab-${m}`}
                onClick={() => { setMode(m); setError(""); }}
                className={`rounded-lg py-2 text-sm font-semibold transition-colors ${
                  mode === m ? "bg-[#ff6a00] text-white" : "text-slate-400 hover:text-white"
                }`}
              >
                {m === "login" ? "Connexion" : "Inscription"}
              </button>
            ))}
          </div>

          {mode === "register" && (
            <div className="mb-4">
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-400">
                Type de compte
              </label>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { k: "client", label: "Client", icon: User, hint: "J'envoie un colis" },
                  { k: "restaurant", label: "Restaurant", icon: Store, hint: "Je fais livrer" },
                ].map((r) => {
                  const Icon = r.icon;
                  const active = role === r.k;
                  return (
                    <button
                      key={r.k}
                      type="button"
                      data-testid={`role-${r.k}-btn`}
                      onClick={() => setRole(r.k)}
                      className={`flex flex-col items-center gap-1 rounded-xl border p-3 transition-all ${
                        active ? "border-[#ff6a00] bg-[#ff6a00]/10" : "border-[#1F293D] hover:border-[#ff6a00]/40"
                      }`}
                    >
                      <Icon className={`h-5 w-5 ${active ? "text-[#ff6a00]" : "text-slate-400"}`} />
                      <span className="text-sm font-bold text-white">{r.label}</span>
                      <span className="text-[10px] text-slate-500">{r.hint}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <form onSubmit={submit} className="space-y-3">
            {mode === "register" && (
              <>
                <Field testId="name-input" placeholder="Nom / Enseigne" value={form.name} onChange={set("name")} required />
                <Field testId="phone-input" placeholder="Téléphone (ex: 33612345678)" value={form.phone} onChange={set("phone")} />
                {role === "restaurant" && (
                  <Field testId="address-input" placeholder="Adresse du restaurant (enlèvement par défaut)" value={form.default_address} onChange={set("default_address")} />
                )}
              </>
            )}
            <Field testId="email-input" type="email" placeholder="Email" value={form.email} onChange={set("email")} required />
            <Field testId="password-input" type="password" placeholder="Mot de passe" value={form.password} onChange={set("password")} required />

            {error && (
              <div data-testid="auth-error" className="rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-xs text-rose-300">
                {error}
              </div>
            )}

            <button
              data-testid="auth-submit-btn"
              type="submit"
              disabled={loading}
              className="flex w-full items-center justify-center gap-2 rounded-full bg-[#ff6a00] py-3 font-bold text-white shadow-[0_0_24px_rgba(255,106,0,0.35)] transition-all hover:bg-[#e85f00] disabled:opacity-60"
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              {mode === "login" ? "Se connecter" : "Créer mon compte"}
            </button>
          </form>

          <div className="my-4 flex items-center gap-3">
            <div className="h-px flex-1 bg-[#1F293D]" />
            <span className="text-xs text-slate-500">ou</span>
            <div className="h-px flex-1 bg-[#1F293D]" />
          </div>

          <button
            data-testid="google-login-btn"
            onClick={googleLogin}
            className="flex w-full items-center justify-center gap-2.5 rounded-full border border-[#1F293D] bg-white py-3 text-sm font-semibold text-slate-800 transition-colors hover:bg-slate-100"
          >
            <GoogleIcon /> Se connecter avec Google
          </button>
        </div>

        <div className="mt-6 grid grid-cols-2 gap-2.5 fade-up">
          <InfoTile icon={Zap} title="COURSE" text="Plis, plats & petits objets · dès 10€" />
          <InfoTile icon={ShoppingBag} title="MARCHANDISE" text="Cartons & colis · prix au poids · dès 15€" />
          <InfoTile icon={Banknote} title="Espèces" text="Paiement à la livraison" />
          <InfoTile icon={MapPin} title="Île-de-France" text="Un seul livreur · 7j/7" />
        </div>
        <div className="mt-4 flex items-center justify-center gap-2 text-xs text-slate-500">
          <MessageCircle className="h-3.5 w-3.5 text-emerald-400" />
          Coordination via WhatsApp après acceptation
        </div>
        <p className="mt-4 text-center text-[11px] text-slate-600">© 2026 CHAP CHAP · Livraison express en Île-de-France</p>
      </div>
    </div>
  );
}

const InfoTile = ({ icon: Icon, title, text }) => (
  <div className="rounded-xl border border-[#1F293D] bg-[#151b2b] p-3">
    <Icon className="mb-1 h-4 w-4 text-[#ff6a00]" />
    <div className="text-sm font-bold text-white">{title}</div>
    <div className="text-[11px] text-slate-400">{text}</div>
  </div>
);

const Field = ({ testId, ...props }) => (
  <input
    data-testid={testId}
    {...props}
    className="w-full rounded-xl border border-[#1F293D] bg-[#0c111c] px-4 py-3 text-sm text-white placeholder:text-slate-600 transition-colors focus:border-[#ff6a00] focus:outline-none"
  />
);
